import { motion } from "framer-motion";
import {
  Database, FileCheck, UserCheck, Activity, ShieldAlert,
  TrendingUp, AlertTriangle, FileText, Zap, ArrowRight,
} from "lucide-react";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] },
});

const PUBLIC_SOURCES = [
  "Company registries & incorporation data",
  "Import/Export shipment data",
  "Financial statements & filings",
  "News, media & adverse records",
  "Sanctions, PEP & watchlists",
  "Industry & market intelligence",
];

const KYC_DOCS = [
  "Certificate of Incorporation",
  "Business licenses & permits",
  "Tax registrations",
  "Bank references",
  "Shareholding structure",
  "Other supporting documents",
];

const ENGINE_PILLARS = [
  { icon: UserCheck, title: "Entity Identification", desc: "Verify existence, ownership & relationship" },
  { icon: TrendingUp, title: "Financial Health Check", desc: "Assess financial strength & stability" },
  { icon: Zap, title: "Commercial Risk Score", desc: "AI-generated score for trust & reliability" },
  { icon: Activity, title: "Credit & Payment Behavior", desc: "Evaluate payment patterns & credit references" },
  { icon: FileCheck, title: "Trade Track Record", desc: "Analyze historical imports/exports & counterparties" },
  { icon: ShieldAlert, title: "Risk & Alert Screening", desc: "Adverse media, sanctions, litigation & compliance red flags" },
];

const OUTPUTS = [
  { title: "Counterparty Intelligence Profile", desc: "Comprehensive view of company background, structure & legitimacy" },
  { title: "Risk & Credit Assessment", desc: "AI scorecard on financial health, credit risk & reliability" },
  { title: "Trade & Performance Insights", desc: "Historical trade behavior, partners, markets & performance trends" },
  { title: "Alerts & Adverse Intelligence", desc: "Real-time alerts on risks, sanctions, litigation & negative news" },
  { title: "Commercial Intelligence Reports", desc: "Detailed reports for deal evaluation, supplier onboarding & monitoring" },
  { title: "Commercial Intelligence via API", desc: "Integrate insights into your systems and workflows" },
];

const IMPACTS = [
  { title: "Reduce Uncertainty", desc: "Make informed decisions with trusted intelligence." },
  { title: "Reduce Risk", desc: "Identify red flags early and avoid costly surprises." },
  { title: "Accelerate Deals", desc: "Faster counterparty evaluation and onboarding." },
  { title: "Improve Profitability", desc: "Work with reliable partners and better terms." },
  { title: "Build Trust", desc: "Stronger relationships through transparency and confidence." },
  { title: "Scale Globally", desc: "Consistent intelligence across markets and geographies." },
];

export function AIConfidencePublic() {
  return (
    <section id="ai-engine" className="relative py-24 md:py-32 bg-[#070F0A] overflow-hidden" data-testid="ai-engine-section">
      <div className="absolute inset-0 [background-image:radial-gradient(circle_at_20%_50%,rgba(34,197,94,0.08),transparent_60%)]" />
      <div className="relative max-w-[1200px] mx-auto px-6">
        <motion.div {...fade(0)} className="flex items-center gap-2 mb-4">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
          <span className="text-[#22C55E] text-[10px] font-semibold tracking-[0.3em] uppercase">AI Confidence Engine</span>
        </motion.div>
        <motion.h2 {...fade(0.05)} className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-white max-w-4xl">
          Commercial intelligence that powers <br className="hidden md:block" />
          <span className="text-[#22C55E]">confident decisions.</span>
        </motion.h2>
        <motion.p {...fade(0.1)} className="mt-6 text-[#C5D5CC] text-base lg:text-lg max-w-3xl leading-relaxed">
          Phase 1 focus: <span className="text-white">Commercial Intelligence</span> — leveraging publicly available data
          and KYC-collected documents to deliver trusted insights for global trade.
        </motion.p>

        {/* 1. INPUTS */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-3">
          <motion.div {...fade(0.1)} className="border border-[#1A2E22] bg-[#09120D] p-6">
            <div className="flex items-center gap-2 mb-3">
              <Database className="w-4 h-4 text-[#22C55E]" />
              <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.2em] font-semibold">1 · Inputs · Public Domain</p>
            </div>
            <ul className="space-y-2">
              {PUBLIC_SOURCES.map((s, i) => (
                <li key={i} className="text-[#C5D5CC] text-sm flex items-start gap-2">
                  <span className="text-[#22C55E] mt-0.5">▸</span>{s}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fade(0.15)} className="border border-[#1A2E22] bg-[#09120D] p-6">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-[#22C55E]" />
              <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.2em] font-semibold">1 · Inputs · KYC Documents</p>
            </div>
            <ul className="space-y-2">
              {KYC_DOCS.map((s, i) => (
                <li key={i} className="text-[#C5D5CC] text-sm flex items-start gap-2">
                  <span className="text-[#22C55E] mt-0.5">▸</span>{s}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* 2. ENGINE PILLARS */}
        <motion.p {...fade(0.2)} className="mt-14 text-[#22C55E] text-[10px] uppercase tracking-[0.2em] font-semibold flex items-center gap-2 mb-5">
          <Zap className="w-3 h-3" /> 2 · Delta GreenX Commercial Intelligence Engine
        </motion.p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {ENGINE_PILLARS.map((p, i) => (
            <motion.div key={p.title} {...fade(0.22 + i * 0.04)}
              className="border border-[#22C55E]/20 bg-[#22C55E]/5 p-5 hover:border-[#22C55E]/50 transition-colors">
              <p.icon className="w-5 h-5 text-[#22C55E] mb-3" />
              <p className="text-white font-['Outfit'] font-semibold text-sm mb-1">{p.title}</p>
              <p className="text-[#A1B2A8] text-xs leading-snug">{p.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* 3. OUTPUTS */}
        <motion.p {...fade(0.4)} className="mt-14 text-[#22C55E] text-[10px] uppercase tracking-[0.2em] font-semibold flex items-center gap-2 mb-5">
          <ArrowRight className="w-3 h-3" /> 3 · Intelligence Outputs · What you get
        </motion.p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {OUTPUTS.map((o, i) => (
            <motion.div key={o.title} {...fade(0.42 + i * 0.04)}
              className="border border-[#1A2E22] bg-[#09120D] p-5 hover:border-[#22C55E]/30 transition-colors">
              <p className="text-white font-['Outfit'] font-semibold text-sm mb-1">{o.title}</p>
              <p className="text-[#A1B2A8] text-xs leading-snug">{o.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* 4. BUSINESS IMPACT */}
        <motion.p {...fade(0.6)} className="mt-14 text-[#22C55E] text-[10px] uppercase tracking-[0.2em] font-semibold flex items-center gap-2 mb-5">
          <TrendingUp className="w-3 h-3" /> 4 · Business Impact
        </motion.p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {IMPACTS.map((m, i) => (
            <motion.div key={m.title} {...fade(0.62 + i * 0.04)}
              className="border border-[#1A2E22] bg-[#09120D] p-4 text-center hover:border-[#22C55E]/40 transition-colors">
              <p className="text-[#22C55E] font-['Outfit'] font-bold text-xs uppercase tracking-wider">{m.title}</p>
              <p className="text-[#A1B2A8] text-[11px] leading-snug mt-2">{m.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.p {...fade(0.8)} className="mt-12 text-[#A1B2A8] text-sm text-center italic max-w-3xl mx-auto leading-relaxed">
          We start with <span className="text-white">Commercial Intelligence</span> — where data is available, reliable and actionable.
          This is the foundation of our AI Confidence Engine, enabling every decision with clarity and confidence.
        </motion.p>
      </div>
    </section>
  );
}
